import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import styles from './index.module.scss';
import SimpleController, { useSimpleController } from './components/SimpleController';
import FullController from './components/FullController';

export type AudioPlayerMode = 'simple' | 'full';

/** 播放状态改变的触发类型 */
export enum PlayStateChangeTrigger {
  /** 用户点击播放/暂停按钮 */
  UserClick = 'user_click',
  /** 音频播放结束 */
  AudioEnded = 'audio_ended',
  /** 外部通过 stopPlaying 控制停止 */
  ExternalStop = 'external_stop',
}

export interface AudioPlayerData {
  playing: boolean;
  trigger: PlayStateChangeTrigger;
  duration: number;
}

/** 提供给 children 的音频控制方法和状态 */
export interface AudioPlayerChildrenProps {
  /** 当前播放状态 */
  playing: boolean;
  /** 当前播放时间（秒） */
  currentTime: number;
  /** 音频总时长（秒） */
  duration: number;
  /** 当前音量 (0-1) */
  volume: number;
  /** 切换播放/暂停 */
  togglePlay: () => void;
  /** 跳转到指定时间 */
  seek: (time: number) => void;
  /** 设置音量 */
  setVolume: (volume: number) => void;
  /** 重置播放器 */
  reset: () => void;
}

export interface AudioPlayerProps {
  /** 音频 URL */
  url: string;
  /** 播放器模式: simple(简单图标模式) | full(完整控制器模式) */
  mode?: AudioPlayerMode;
  /** 自定义类名 */
  className?: string;
  /** 自定义样式 */
  style?: React.CSSProperties;
  /** 是否停止播放（外部控制） */
  stopPlaying?: boolean;
  /** 播放状态改变回调 */
  onPlayStateChange?: (data: AudioPlayerData) => void;
  /** 自定义子内容（显示在控制器上方），会接收 AudioPlayerChildrenProps */
  children?: ((props: AudioPlayerChildrenProps) => React.ReactNode) | React.ReactNode;
  /** 是否自动隐藏控制器（仅 full 模式有效） */
  autoHideController?: boolean;
  /** 是否显示下载按钮（仅 full 模式有效） */
  showDownload?: boolean;
  /** 透传到底层 audio 元素的 crossOrigin 属性 */
  crossOrigin?: '' | 'anonymous' | 'use-credentials';
  /** 初始音量 (0-1)，默认保持现有播放器的 0.5 */
  initialVolume?: number;
  /** 下载回调 */
  onDownload?: (data: { duration: number; playing: boolean; }) => void;
  hiddenDefaultController?: boolean;
}

export default function AudioPlayer({
  url,
  mode = 'simple',
  className,
  style,
  stopPlaying = false,
  children,
  autoHideController = true,
  showDownload = true,
  onPlayStateChange,
  onDownload,
  hiddenDefaultController,
  crossOrigin = 'anonymous',
  initialVolume = 0.5,
}: AudioPlayerProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(() => Math.min(Math.max(initialVolume, 0), 1));
  const [showController, setShowController] = useState(!autoHideController);
  const audioRef = useRef<HTMLAudioElement>(null);
  const isHoveringRef = useRef(false);
  const onPlayStateChangeRef = useRef(onPlayStateChange);
  const simpleControllerRef = useSimpleController();
  const rafIdRef = useRef<number | null>(null); // 用于存储 requestAnimationFrame ID

  // 更新回调引用
  useEffect(() => {
    onPlayStateChangeRef.current = onPlayStateChange;
  }, [onPlayStateChange]);

  // 保持控制器展示音量与原生 audio 元素的实际音量一致。
  useEffect(() => {
    const nextVolume = Math.min(Math.max(initialVolume, 0), 1);
    setVolume(nextVolume);
    if (audioRef.current) {
      audioRef.current.volume = nextVolume;
    }
  }, [initialVolume, url]);

  // 音频加载完成 - 更新时长
  const updateDuration = useCallback(() => {
    if (audioRef.current && audioRef.current.duration) {
      const audioDuration = audioRef.current.duration;
      // 检查 duration 是否为有效值（不是 NaN 或 Infinity）
      if (isFinite(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
      }
    }
  }, []);

  // 使用 requestAnimationFrame 持续更新时间（解决 iOS 设备 timeupdate 事件不触发问题）
  const updateTimeLoop = useCallback(() => {
    if (audioRef.current && !audioRef.current.paused && !audioRef.current.ended) {
      setCurrentTime(audioRef.current.currentTime);
      rafIdRef.current = requestAnimationFrame(updateTimeLoop);
    }
  }, []);

  // 停止时间更新循环
  const stopTimeLoop = useCallback(() => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  // 当 URL 变化时，重置状态
  useEffect(() => {
    stopTimeLoop(); // 停止时间更新循环
    setDuration(0);
    setCurrentTime(0);
    setPlaying(false);
    // 如果音频元素存在，重新加载音频
    if (audioRef.current) {
      audioRef.current.load();
    }
  }, [url, stopTimeLoop]);

  // 播放/暂停控制
  const togglePlay = () => {
    if (!audioRef.current) return;
    const nextPlaying = !playing;
    if (nextPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
    setPlaying(nextPlaying);
    onPlayStateChangeRef.current?.({ playing: nextPlaying, trigger: PlayStateChangeTrigger.UserClick, duration: audioRef.current?.duration || 0 });
  };

  // 拖动进度
  const handleSeek = (time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  // 音量控制
  const handleVolumeChange = (value: number) => {
    if (!audioRef.current) return;
    audioRef.current.volume = value;
    setVolume(value);
  };

  // 音频元数据加载完成
  const handleLoadedMetadata = () => {
    updateDuration();
  };

  // 音频数据加载完成（备用方案）
  const handleLoadedData = () => {
    updateDuration();
  };

  // 时长改变事件（备用方案）
  const handleDurationChange = () => {
    updateDuration();
  };

  // 音频加载错误
  const handleError = (e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
    console.error('音频加载错误:', e.currentTarget.error);
    setPlaying(false); // 停止播放状态
  };

  // 时间更新（备用，iOS 上不可靠）
  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current?.currentTime || 0);
  };

  // 播放结束
  const handleAudioEnded = () => {
    stopTimeLoop(); // 停止时间更新循环
    setPlaying(false);
    setCurrentTime(0);
    simpleControllerRef.current?.reset();
    onPlayStateChangeRef.current?.({ playing: false, trigger: PlayStateChangeTrigger.AudioEnded, duration: audioRef.current?.duration || 0 });
  };

  // 监听音频实际播放状态（用于同步锁屏控制等外部操作）
  const handlePlay = () => {
    setPlaying(true);
    stopTimeLoop(); // 先停止之前的循环（如果有）
    rafIdRef.current = requestAnimationFrame(updateTimeLoop); // 开始时间更新循环
  };

  const handlePause = () => {
    setPlaying(false);
    stopTimeLoop(); // 停止时间更新循环
  };

  // 重置播放器状态
  const resetPlayer = useCallback(() => {
    simpleControllerRef.current?.reset();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setPlaying(false);
    setCurrentTime(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 鼠标移入（仅 full 模式）
  const handleMouseEnter = () => {
    if (mode !== 'full') return;
    isHoveringRef.current = true;
    if (autoHideController) {
      setShowController(true);
    }
  };

  // 鼠标移出（仅 full 模式）
  const handleMouseLeave = () => {
    if (mode !== 'full') return;
    isHoveringRef.current = false;
    if (autoHideController) {
      setShowController(playing);
    }
  };

  // 下载音频
  const handleDownload = () => {
    onDownload?.({ duration, playing });
  };

  // 监听外部停止播放信号
  useEffect(() => {
    if (stopPlaying && playing) {
      resetPlayer();
      stopTimeLoop(); // 停止时间更新循环
      onPlayStateChangeRef.current?.({ playing: false, trigger: PlayStateChangeTrigger.ExternalStop, duration: audioRef.current?.duration || 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopPlaying, playing]);

  // 监听播放状态，自动控制控制器显示（仅 full 模式且 autoHide）
  useEffect(() => {
    if (mode === 'full' && autoHideController && !isHoveringRef.current) {
      setShowController(playing);
    }
  }, [playing, autoHideController, mode]);

  // 组件卸载时清理 requestAnimationFrame
  useEffect(() => {
    return () => {
      stopTimeLoop();
    };
  }, [stopTimeLoop]);

  // 构造传递给 children 的控制方法和状态
  const childrenProps: AudioPlayerChildrenProps = {
    playing,
    currentTime,
    duration,
    volume,
    togglePlay,
    seek: handleSeek,
    setVolume: handleVolumeChange,
    reset: resetPlayer,
  };

  // 渲染 children
  const renderChildren = () => {
    if (!children) return null;
    if (typeof children === 'function') {
      return children(childrenProps);
    }
    return children;
  };

  return (
    <div
      className={cn(
        styles.audioPlayer,
        styles[`mode-${mode}`],
        className,
      )}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <audio
        src={url}
        ref={audioRef}
        preload="metadata"
        crossOrigin={crossOrigin}
        onLoadedMetadata={handleLoadedMetadata}
        onLoadedData={handleLoadedData}
        onDurationChange={handleDurationChange}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleAudioEnded}
        onError={handleError}
        onPlay={handlePlay}
        onPause={handlePause}
      >
        <track kind="captions" />
      </audio>

      {renderChildren()}

      {!hiddenDefaultController && (
        <>
          {mode === 'simple' && (
          <SimpleController
            ref={simpleControllerRef}
            playing={playing}
            currentTime={currentTime}
            duration={duration}
            showDownload={showDownload}
            onTogglePlay={togglePlay}
            onReset={resetPlayer}
            onDownload={handleDownload}
          />
          )}
          {mode === 'full' && (
          <FullController
            visible={showController}
            playing={playing}
            currentTime={currentTime}
            duration={duration}
            volume={volume}
            showDownload={showDownload}
            onDownload={handleDownload}
            onTogglePlay={togglePlay}
            onSeek={handleSeek}
            onVolumeChange={handleVolumeChange}
          />
          )}
        </>
      )}

    </div>
  );
}
